import React from 'react';
import { cn } from '../../utils/cn';

/**
 * Reusable Button component using our design system
 * 
 * @param {Object} props
 * @param {'primary'|'secondary'|'success'|'warning'|'danger'|'nav'|'navActive'|'gradientPurple'|'gradientBlue'|'gradientPink'|'gradientGreen'} props.variant - Button style variant
 * @param {'compact'|'normal'|'large'} props.size - Button size
 * @param {React.ComponentType} props.icon - Optional icon component
 * @param {boolean} props.loading - Show loading state
 * @param {React.ReactNode} props.children - Button content
 * @param {string} props.className - Additional CSS classes
 * @param {Object} props.rest - Additional button props
 */
const Button = ({
  variant = 'primary',
  size = 'compact',
  icon: Icon,
  loading = false,
  children,
  className,
  disabled,
  ...rest
}) => {
  // Base classes for all buttons
  const baseClasses = 'inline-flex items-center justify-center font-medium transition-all duration-150 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed rounded';
  
  // Size classes
  const sizeClasses = {
    compact: 'px-2 py-1 text-xs gap-1',
    normal: 'px-3 py-2 text-sm gap-2', 
    large: 'px-4 py-3 text-base gap-2'
  };
  
  // Variant classes with actual Tailwind colors
  const variantClasses = {
    primary: 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500',
    secondary: 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 focus:ring-gray-500',
    success: 'bg-green-600 text-white hover:bg-green-700 focus:ring-green-500',
    warning: 'bg-orange-600 text-white hover:bg-orange-700 focus:ring-orange-500',
    danger: 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500',
    nav: 'text-gray-600 hover:bg-gray-100 focus:ring-gray-500',
    navActive: 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500',
    gradientPurple: 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700 focus:ring-purple-500',
    gradientBlue: 'bg-gradient-to-r from-blue-600 to-teal-600 text-white hover:from-blue-700 hover:to-teal-700 focus:ring-blue-500',
    gradientPink: 'bg-gradient-to-r from-pink-600 to-rose-600 text-white hover:from-pink-700 hover:to-rose-700 focus:ring-pink-500',
    gradientGreen: 'bg-gradient-to-r from-green-600 to-emerald-600 text-white hover:from-green-700 hover:to-emerald-700 focus:ring-green-500'
  };
  
  const isDisabled = disabled || loading;
  
  return (
    <button
      className={cn(
        baseClasses,
        sizeClasses[size],
        variantClasses[variant],
        className
      )}
      disabled={isDisabled}
      {...rest}
    >
      {loading ? (
        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white" />
      ) : Icon ? (
        <Icon className="h-3 w-3" />
      ) : null}
      {children}
    </button>
  );
};

// Predefined button variants for common use cases
export const PrimaryButton = (props) => <Button variant="primary" {...props} />;
export const SecondaryButton = (props) => <Button variant="secondary" {...props} />;
export const SuccessButton = (props) => <Button variant="success" {...props} />;
export const WarningButton = (props) => <Button variant="warning" {...props} />;
export const DangerButton = (props) => <Button variant="danger" {...props} />;

// Size variants
export const CompactButton = (props) => <Button size="compact" {...props} />;
export const NormalButton = (props) => <Button size="normal" {...props} />;
export const LargeButton = (props) => <Button size="large" {...props} />;

// Navigation variants
export const NavButton = (props) => <Button variant="nav" {...props} />;
export const NavActiveButton = (props) => <Button variant="navActive" {...props} />;

// Gradient variants for special UI elements
export const GradientPurpleButton = (props) => <Button variant="gradientPurple" {...props} />;
export const GradientBlueButton = (props) => <Button variant="gradientBlue" {...props} />;
export const GradientPinkButton = (props) => <Button variant="gradientPink" {...props} />;
export const GradientGreenButton = (props) => <Button variant="gradientGreen" {...props} />;

export default Button;